'use client'

import React, { useState, useRef } from 'react'
import type { Form, StepperBlock as StepperBlockType } from '@/payload-types'
import { FormRenderer } from '@/components/FormRenderer'
import { InlineFormRenderer } from '@/components/InlineFormRenderer'
import { PillCap } from '@/components/ui/PillCap'
import { Media } from '@/components/Media'
import './stepper.scss'

type StepperStep = NonNullable<StepperBlockType['steps']>[number]

// Steps are hydrated server-side with the referenced form document (see Component.tsx)
type HydratedStep = StepperStep & { formData?: Form }

export const StepperBlock: React.FC<StepperBlockType> = ({
  color = 'primary',
  centered = false,
  layoutElements,
  steps,
  image,
  stepperWidth = '8',
  stepperPosition = 'left',
  finalScreen,
}) => {
  const [activeStep, setActiveStep] = useState<number>(0)
  const [allFormData, setAllFormData] = useState<Record<string, unknown>>({})
  const [isCompleted, setIsCompleted] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const imageRef = useRef<HTMLDivElement>(null)

  if (!steps || steps.length === 0) {
    return null
  }

  // Convert color to PascalCase for CSS class
  const colorValue = color || 'primary'
  const colorClass = `color${colorValue.charAt(0).toUpperCase() + colorValue.slice(1)}`

  // Calculate image column width based on stepper width
  const imageWidth = stepperWidth === '6' ? '6' : '4'

  // Generate classes for stepper
  const stepperClasses = ['stepper', 'transparent', colorClass, centered ? 'center' : '']
    .filter(Boolean)
    .join(' ')

  // Render title
  const renderTitle = () => {
    return (
      <h3 className="stepperTitle" key="title">
        {steps[activeStep]?.title}
      </h3>
    )
  }

  // Render description (only if not empty)
  const renderDescription = () => {
    const desc = steps[activeStep]?.description
    if (!desc || desc.trim() === '') return null

    return (
      <p className="stepperDesc" key="description">
        {desc}
      </p>
    )
  }

  // Render list of steps
  const renderList = () => {
    // If completed, show configured icon
    if (isCompleted) {
      const iconClass = finalScreen?.icon || 'icon-thumbs-up'
      return (
        <div className="olStepper completed p-0" key="list">
          <i className={`icon ${iconClass}`}></i>
        </div>
      )
    }

    // Normal stepper view
    return (
      <ol className="olStepper p-0" key="list">
        {steps.map((_, index) => {
          const isActive = activeStep === index

          return (
            <li key={index} className={`stepperItem ${isActive ? 'active' : ''}`}>
              <span></span>
            </li>
          )
        })}
      </ol>
    )
  }

  // Get ordered elements based on layoutElements array
  const getOrderedElements = () => {
    const elements = {
      title: renderTitle(),
      description: renderDescription(),
      list: renderList(),
    }

    // Default order if layoutElements is not set
    const defaultOrder = [{ element: 'title' }, { element: 'description' }, { element: 'list' }]

    const order = (Array.isArray(layoutElements) ? layoutElements : defaultOrder) as {
      element: string
    }[]

    return order
      .map((item) => {
        const elementType = item.element as keyof typeof elements
        return elements[elementType]
      })
      .filter(Boolean)
  }

  // Handle intermediate step validation (not submission)
  const handleStepValidation = (stepData: Record<string, unknown>) => {
    // Store data from this step
    setAllFormData((prev) => ({
      ...prev,
      ...stepData,
    }))

    // Move to next step
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1)
    }
  }

  // Handle final form submission (last step) — persists into the form-builder
  // "Form Submissions" collection via /api/stepper-submit.
  const handleFinalSubmit = async (finalStepData: Record<string, unknown>) => {
    const completeFormData = {
      ...allFormData,
      ...finalStepData,
    }

    setSubmitError(null)

    // Map field names → human labels (across every step) for readable submissions.
    const fieldLabels: Record<string, string> = {}
    steps.forEach((step) => {
      const stepFields = step.fields as
        | Array<{ name?: string | null; label?: string | null }>
        | null
        | undefined
      stepFields?.forEach((field) => {
        const key = field.name || field.label?.toLowerCase().replace(/\s+/g, '-') || ''
        if (key) fieldLabels[key] = field.label || key
      })
    })

    const submissionData = Object.fromEntries(
      Object.entries(completeFormData).map(([key, value]) => [fieldLabels[key] || key, value]),
    )

    // Real-form steppers submit against their own Form; inline steppers are keyed by name.
    const currentStep = steps[activeStep]
    const hydratedForm = (currentStep as HydratedStep).formData
    const requestBody =
      currentStep?.contentType === 'form' && hydratedForm
        ? { formId: hydratedForm.id, submissionData }
        : { formTitle: steps[0]?.title?.trim() || 'Formulaire', submissionData }

    try {
      const response = await fetch('/api/stepper-submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorBody = (await response.json().catch(() => null)) as { error?: string } | null
        throw new Error(errorBody?.error || 'Erreur lors de la soumission du formulaire')
      }

      // Success — show the configured final screen (no more native alert).
      setIsCompleted(true)
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'Une erreur est survenue. Veuillez réessayer.',
      )
    }
  }

  // Render step content (text or form)
  const renderStepContent = () => {
    // If completed, show configured success message and button
    if (isCompleted) {
      const message = finalScreen?.message || 'Merci ! Votre formulaire a été soumis avec succès.'
      const buttonText = finalScreen?.buttonText
      const buttonUrl = finalScreen?.buttonUrl
      const showButton = buttonText && buttonUrl

      return (
        <div className="text-center my-5">
          <p className="text-white mb-4" style={{ whiteSpace: 'pre-line' }}>
            {message}
          </p>
          {showButton && (
            <a
              href={buttonUrl}
              className={`btn btn-pill btn-pill-tint-${colorValue} btn-pill-lg`}
            >
              {(buttonText || '').replace(/[\s→➔➜➡»›>]+$/u, '').trim()}
              <PillCap />
            </a>
          )}
        </div>
      )
    }

    const isFirstStep = activeStep === 0
    const isLastStep = activeStep === steps.length - 1
    const currentStep = steps[activeStep]

    if (!currentStep) {
      return <div className="text-center my-5">Aucun contenu pour cette étape</div>
    }

    // If it's a formBuilder type with inline fields
    if (currentStep.contentType === 'formBuilder' && currentStep.fields) {
      return (
        <div className="my-4">
          <InlineFormRenderer
            key={activeStep}
            fields={currentStep.fields}
            onValidate={isLastStep ? undefined : handleStepValidation}
            onSubmitSuccess={isLastStep ? handleFinalSubmit : undefined}
            isLastStep={isLastStep}
            overrideColor={color}
            onBack={() => setActiveStep(activeStep - 1)}
            showBack={!isFirstStep}
          />
        </div>
      )
    }

    // If it's a form type and formData exists
    const hydratedStep = currentStep as HydratedStep
    if (currentStep.contentType === 'form' && hydratedStep.formData) {
      return (
        <div className="my-4">
          <FormRenderer
            key={activeStep}
            form={hydratedStep.formData}
            onValidate={isLastStep ? undefined : handleStepValidation}
            onSubmitSuccess={isLastStep ? handleFinalSubmit : undefined}
            isLastStep={isLastStep}
            overrideColor={color}
            onBack={() => setActiveStep(activeStep - 1)}
            showBack={!isFirstStep}
          />
        </div>
      )
    }

    // If it's a form type but formData doesn't exist (shouldn't happen)
    if (currentStep.contentType === 'form') {
      return <div className="alert alert-warning my-4">Formulaire non disponible</div>
    }

    // If it's a formBuilder type but no fields defined
    if (currentStep.contentType === 'formBuilder') {
      return <div className="alert alert-warning my-4">Aucun champ défini pour cette étape</div>
    }

    // Default: text content
    return (
      <div className="text-center my-5">
        {currentStep.content || 'Aucun contenu pour cette étape'}
      </div>
    )
  }

  // Render sticky image
  const renderStickyImage = () => {
    if (!image || typeof image === 'string') {
      return null
    }

    return (
      <div ref={imageRef} className="stepperStickyImage" style={{ top: '80px' }}>
        <Media resource={image} imgClassName="" />
      </div>
    )
  }

  // Render stepper content column
  const renderStepperContent = () => {
    const isFirstStep = activeStep === 0
    const isLastStep = activeStep === steps.length - 1
    const currentStep = steps[activeStep]

    // Form steps render their own footer (Précédent + Suivant/Envoyer) inside the
    // <form> so submission and native validation work. Only non-form steps (plain
    // text/content) fall back to the external navigation buttons below.
    const isFormStep = Boolean(
      (currentStep?.contentType === 'formBuilder' && currentStep?.fields?.length) ||
        (currentStep?.contentType === 'form' && (currentStep as HydratedStep).formData),
    )

    return (
      <>
        <div className={stepperClasses}>{getOrderedElements()}</div>

        <div className="stepDetails">
          {renderStepContent()}

          {submitError && <div className="alert alert-danger my-3">{submitError}</div>}

          {!isCompleted && !isFormStep && (
            <div className="formFooter">
              {!isFirstStep && (
                <button
                  type="button"
                  className={`btn btn-sm btn-${colorValue}`}
                  onClick={() => setActiveStep(activeStep - 1)}
                >
                  Précédent
                </button>
              )}
              {!isLastStep && (
                <button
                  type="button"
                  className={`btn btn-sm btn-${colorValue}`}
                  onClick={() => setActiveStep(activeStep + 1)}
                >
                  Suivant
                </button>
              )}
            </div>
          )}
        </div>
      </>
    )
  }

  return (
    <div className="stepperContainer">
      <div className="row g-4">
        {stepperPosition === 'left' ? (
          <>
            <div className={`col-lg-${stepperWidth}`}>{renderStepperContent()}</div>
            <div className={`col-lg-${imageWidth}`}>{renderStickyImage()}</div>
          </>
        ) : (
          <>
            <div className={`col-lg-${imageWidth}`}>{renderStickyImage()}</div>
            <div className={`col-lg-${stepperWidth}`}>{renderStepperContent()}</div>
          </>
        )}
      </div>
    </div>
  )
}
